export interface MapPreviewSourceBoundsRecord {
  id: string;
  bounds?: number[] | null;
}

export interface MapPreviewViewState {
  center?: [number, number];
  zoom?: number;
}

const normalizeBoundsTuple = (value: unknown): [number, number, number, number] | null => {
  if (!Array.isArray(value) || value.length !== 4) {
    return null;
  }
  const bounds = value.map(entry => Number(entry));
  if (bounds.some(entry => !Number.isFinite(entry))) {
    return null;
  }
  return [bounds[0], bounds[1], bounds[2], bounds[3]];
};

const mergeBoundsTuple = (
  current: [number, number, number, number] | null,
  next: [number, number, number, number] | null,
): [number, number, number, number] | null => {
  if (!next) {
    return current;
  }
  if (!current) {
    return [...next];
  }
  return [
    Math.min(current[0], next[0]),
    Math.min(current[1], next[1]),
    Math.max(current[2], next[2]),
    Math.max(current[3], next[3]),
  ];
};

const estimatePreviewZoomFromBounds = (bounds: [number, number, number, number]) => {
  const width = Math.abs(bounds[2] - bounds[0]);
  const height = Math.abs(bounds[3] - bounds[1]);
  if (width <= 0 && height <= 0) {
    return null;
  }
  const maxDim = Math.max(width, height);
  if (maxDim <= 0) {
    return null;
  }
  const zoom = Math.log2(360 / maxDim);
  return Math.max(0, Math.min(12, zoom));
};

const resolveMapSourceIdFromStyleSource = (styleSource?: Record<string, unknown> | null) => {
  if (!styleSource || typeof styleSource !== 'object') {
    return '';
  }

  const rawUrl = typeof styleSource.url === 'string' ? styleSource.url.trim() : '';
  if (rawUrl.startsWith('pmtiles://')) {
    return rawUrl.slice('pmtiles://'.length).split('?', 1)[0].trim();
  }

  const rawData = typeof styleSource.data === 'string' ? styleSource.data.trim() : '';
  if (rawData.startsWith('geojson://')) {
    return rawData.slice('geojson://'.length).split('?', 1)[0].trim();
  }

  const runtimeGeoJSONMatch = rawData.match(/^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/data\.geojson(?:$|[?#])/);
  if (runtimeGeoJSONMatch?.[1]) {
    return decodeURIComponent(runtimeGeoJSONMatch[1]);
  }

  const tiles = Array.isArray(styleSource.tiles) ? styleSource.tiles : [];
  const tileTemplate = tiles.find((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
  const tileMatch = tileTemplate?.match(/^\/api\/v1(?:\/mapping)?\/map-sources\/([^/]+)\/tiles\/\{z\}\/\{x\}\/\{y\}(?:$|[?#])/);
  return tileMatch?.[1] ? decodeURIComponent(tileMatch[1]) : '';
};

export const derivePreviewViewFromStyleBounds = (
  styleJson: Record<string, unknown>,
  sources: MapPreviewSourceBoundsRecord[],
): MapPreviewViewState | null => {
  const styleSources =
    styleJson.sources && typeof styleJson.sources === 'object'
      ? (styleJson.sources as Record<string, Record<string, unknown>>)
      : {};
  const sourceLookup = new Map(
    sources
      .filter((source): source is MapPreviewSourceBoundsRecord & { id: string } => typeof source.id === 'string' && source.id.trim().length > 0)
      .map(source => [source.id, source]),
  );

  let mergedBounds: [number, number, number, number] | null = null;
  Object.values(styleSources).forEach(styleSource => {
    const sourceId = resolveMapSourceIdFromStyleSource(styleSource);
    if (!sourceId) {
      return;
    }
    mergedBounds = mergeBoundsTuple(mergedBounds, normalizeBoundsTuple(sourceLookup.get(sourceId)?.bounds));
  });

  if (!mergedBounds) {
    return null;
  }

  return {
    center: [(mergedBounds[0] + mergedBounds[2]) / 2, (mergedBounds[1] + mergedBounds[3]) / 2],
    zoom: estimatePreviewZoomFromBounds(mergedBounds) ?? undefined,
  };
};
